import {compact} from '@benchling/js-utils';
import React from 'react';

import {assert} from 'Asserts';
import {NestedCardRowType} from 'config-migration/import/import-plan-sections/utils/childImportPlanUtils';
import {LogLevel} from 'config-migration/migration-log/LogLevel';
import {HighImpactChange, MigrationError} from 'config-migration/MigrationConstants';
import {PrimaryObjectPlan} from 'config-migration/types/MigrationObjects';
import {PrimaryObjectType} from 'config-migration/types/ObjectType';
import notifications from 'notifications';

import {getActionOrigin, trackTriggeredConfigMigrationErrorToast} from '../../analytics';
import {useConfigMigrationImportTabContext} from '../../contexts/ConfigMigrationImportTabContext';
import {useConfigMigrationPageContext} from '../../contexts/ConfigMigrationPageContext';
import {ObjectAction, ObjectActionType, UpdateAction} from '../../types/objectActions';
import {countChildHighImpactChanges} from '../HighImpactChanges';
import ChildImportPlanSection, {
  ChildImportPlanSectionWrapper,
} from '../import-plan-sections/ChildImportPlanSection';
import {ImportPlanSectionWrapper} from '../import-plan-sections/ImportPlanSection';
import ImportPlanSectionRow, {
  ImportPlanSectionRowWrapper,
  ObjectActionDropdownColumn,
  ObjectDisplayNameColumn,
} from '../import-plan-sections/ImportPlanSectionRow';

export interface ProcessTaskSchemaPlanSectionProps {
  sectionDisplayName: string;
  plans: Array<PrimaryObjectPlan>;
  onUpdatePlans: (MigrationObjectPlan: Array<PrimaryObjectPlan>) => void;
  collapsed: boolean;
  onUpdateCollapsed: (collapsed: boolean) => void;
  errorsByObjectId: {[objectId: string]: Array<MigrationError>};
  highImpactChangeMap: Map<string, Array<HighImpactChange>>;
}

interface MemberObjectPlanAndError {
  memberPlan: PrimaryObjectPlan;
  memberErrors: Array<MigrationError>;
}

export interface ProcessTaskSchemaPlanRowProps {
  plan: PrimaryObjectPlan;
  memberPlansAndErrors: Array<MemberObjectPlanAndError>;
  errors: Array<MigrationError>;
  onUpdatePlans: (plans: Array<PrimaryObjectPlan>) => void;
  highImpactChangeMap: Map<string, Array<HighImpactChange>>;
}

export function deriveFlowchartConfigActionFromTaskSchemaAction(
  taskSchemaPlan: PrimaryObjectPlan,
  flowchartConfigPlan: PrimaryObjectPlan
): PrimaryObjectPlan {
  let flowchartConfigAction: ObjectAction;
  if (
    taskSchemaPlan.selectedAction.type === ObjectActionType.CREATE ||
    taskSchemaPlan.selectedAction.type === ObjectActionType.IGNORE
  ) {
    flowchartConfigAction = {type: taskSchemaPlan.selectedAction.type};
  } else if (taskSchemaPlan.selectedAction.type === ObjectActionType.UPDATE) {
    // FlowchartConfigVersions are immutable. So if the task schema is being updated and the flowchart config
    // version is included in the migration plan, there should not be a provenance URI match.
    flowchartConfigAction = flowchartConfigPlan.initialAction;
  } else {
    assert(false, `Unsupported selected action ${taskSchemaPlan.selectedAction.type}`);
  }
  return {
    ...flowchartConfigPlan,
    selectedAction: flowchartConfigAction,
    childMigrationObjectsByType: new Map(),
  };
}

type PlanWithSelectedUpdateAction = PrimaryObjectPlan & {selectedAction: UpdateAction};

function isPlanWithSelectedUpdateAction(plan: PrimaryObjectPlan): plan is PlanWithSelectedUpdateAction {
  return plan.selectedAction.type === ObjectActionType.UPDATE;
}

function findCorrespondingUpdateAction(
  taskSchemaPlan: PlanWithSelectedUpdateAction,
  memberPlan: PrimaryObjectPlan
): ObjectAction | null {
  // Member objects like output schemas and flowchart configs are defined when the task schema
  // is created. If the action's provenance URI matches the task schema then we're updating a task schema on the
  // destination org that has already been migrated from this task schema. So the member objects must
  // also exist on the destination org and we can select their initial action which will be to update the
  // member object.
  if (taskSchemaPlan.selectedAction.target.provenanceUri === taskSchemaPlan.objectId) {
    return memberPlan.initialAction;
  }
  let correspondingAction: ObjectAction | null =
    memberPlan.additionalActions.find(
      (action) =>
        action.type === taskSchemaPlan.selectedAction.type &&
        action.target.parentIdentifier?.apiIdentifier === taskSchemaPlan.selectedAction.target.apiIdentifier
    ) ?? null;

  if (correspondingAction == null) {
    // If the task schema plan is set to update and there is no corresponding update action for the member plan
    // then we have not yet migrated the member object. So we need to create it instead.
    if (taskSchemaPlan.selectedAction.type === ObjectActionType.UPDATE) {
      correspondingAction = {type: ObjectActionType.CREATE};
    }
  }
  return correspondingAction;
}

export function deriveActionFromTaskSchemaActionForMutableMemberObject(
  taskSchemaPlan: PrimaryObjectPlan,
  memberPlan: PrimaryObjectPlan
): PrimaryObjectPlan {
  let memberAction: ObjectAction;
  if (
    taskSchemaPlan.selectedAction.type === ObjectActionType.CREATE ||
    taskSchemaPlan.selectedAction.type === ObjectActionType.IGNORE
  ) {
    memberAction = {type: taskSchemaPlan.selectedAction.type};
  } else if (isPlanWithSelectedUpdateAction(taskSchemaPlan)) {
    const correspondingAction = findCorrespondingUpdateAction(taskSchemaPlan, memberPlan);
    if (correspondingAction == null) {
      throw new Error(
        `Could not find corresponding ${memberPlan.objectType} action for ${taskSchemaPlan.selectedAction.target.displayName}`
      );
    }
    memberAction = correspondingAction;
  } else {
    assert(false, `Unsupported selected action ${taskSchemaPlan.selectedAction.type}`);
  }
  return {
    ...memberPlan,
    selectedAction: memberAction,
    childMigrationObjectsByType: new Map(),
  };
}

interface MemberPlanConfig {
  title: string;
  deriveUpdateFromTaskSchemaPlan: (
    taskSchemaPlan: PrimaryObjectPlan,
    memberPlan: PrimaryObjectPlan
  ) => PrimaryObjectPlan;
}

const MEMBER_PLAN_CONFIGS_BY_TYPE: {[objectType in PrimaryObjectType]?: MemberPlanConfig} = {
  [PrimaryObjectType.ProcessOutputSchema]: {
    title: 'Output schema',
    deriveUpdateFromTaskSchemaPlan: deriveActionFromTaskSchemaActionForMutableMemberObject,
  },
  [PrimaryObjectType.ProcessFlowchartConfigVersion]: {
    title: 'Flowchart configuration',
    deriveUpdateFromTaskSchemaPlan: deriveFlowchartConfigActionFromTaskSchemaAction,
  },
  [PrimaryObjectType.RequestV2Definition]: {
    title: 'Request templates',
    deriveUpdateFromTaskSchemaPlan: deriveActionFromTaskSchemaActionForMutableMemberObject,
  },
  [PrimaryObjectType.WorkflowTaskDefinition]: {
    title: 'Task definitions',
    deriveUpdateFromTaskSchemaPlan: deriveActionFromTaskSchemaActionForMutableMemberObject,
  },
  [PrimaryObjectType.WorkflowTaskDefinitionVersion]: {
    title: 'Task definition versions',
    deriveUpdateFromTaskSchemaPlan: deriveFlowchartConfigActionFromTaskSchemaAction,
  },
};

export function ProcessTaskSchemaPlanRow(props: ProcessTaskSchemaPlanRowProps): JSX.Element {
  const hasErrors = props.errors.filter(({level}) => level === LogLevel.ERROR).length > 0;
  const hasWarnings = props.errors.filter(({level}) => level === LogLevel.WARNING).length > 0;
  const [isCollapsed, setIsCollapsed] = React.useState<boolean>(true);

  const {activeTab, eventGroupId} = useConfigMigrationPageContext();
  const {activeStep, destinationOrganizationId} = useConfigMigrationImportTabContext();
  const highImpactChangeCount = countChildHighImpactChanges(props.plan, props.highImpactChangeMap);

  return (
    <>
      <ImportPlanSectionRowWrapper
        plan={props.plan}
        hasErrors={hasErrors}
        hasWarnings={hasWarnings}
        key={props.plan.objectId}
        nestedDepth={0}
      >
        <ObjectDisplayNameColumn
          hasErrors={hasErrors}
          hasWarnings={hasWarnings}
          plan={props.plan}
          errors={props.errors}
          setIsCollapsed={setIsCollapsed}
          isCollapsed={isCollapsed}
          hasChildren={
            props.plan.childMigrationObjectsByType.size > 0 ||
            (props.memberPlansAndErrors.length > 0 &&
              props.plan.selectedAction.type !== ObjectActionType.IGNORE)
          }
          highImpactChangeCount={highImpactChangeCount}
        />
        <ObjectActionDropdownColumn
          plan={props.plan}
          showIgnore={true}
          onUpdatePlan={(updatedPlan) => {
            try {
              const updatedMemberPlans = props.memberPlansAndErrors.map(({memberPlan}) =>
                MEMBER_PLAN_CONFIGS_BY_TYPE[memberPlan.objectType]!.deriveUpdateFromTaskSchemaPlan(
                  updatedPlan,
                  memberPlan
                )
              );
              props.onUpdatePlans([updatedPlan, ...updatedMemberPlans]);
              // eslint-disable-next-line benchlint/always-throw-from-catch
            } catch (e) {
              notifications.error(String(e));
              trackTriggeredConfigMigrationErrorToast({
                action_origin: getActionOrigin(activeTab),
                destination_organization_id: destinationOrganizationId,
                event_group_id: eventGroupId,
                stage: activeTab,
                import_step: activeStep,
              });
            }
          }}
        />
      </ImportPlanSectionRowWrapper>
      {!isCollapsed &&
        props.plan.childMigrationObjectsByType.size > 0 &&
        Array.from(props.plan.childMigrationObjectsByType.keys()).map((childType) => (
          <ChildImportPlanSection
            plan={props.plan}
            childType={childType}
            onUpdatePlan={(plan) => {
              props.onUpdatePlans([plan]);
            }}
            highImpactChangeMap={props.highImpactChangeMap}
            nestedDepth={0}
            key={childType}
          />
        ))}
      {!isCollapsed &&
        props.plan.selectedAction.type !== ObjectActionType.IGNORE &&
        Object.entries(MEMBER_PLAN_CONFIGS_BY_TYPE).map(([objectType, config]) => {
          const memberPlans = props.memberPlansAndErrors.filter(
            ({memberPlan}) => memberPlan.objectType === objectType
          );
          return (
            memberPlans.length > 0 && (
              <ChildImportPlanSectionWrapper
                key={objectType}
                title={config.title}
                dataTestAttrs={{
                  component: 'ProcessTaskSchemaPlanRow',
                  element: 'planSectionWrapper',
                  key: objectType,
                }}
                nestedDepth={0}
                nestedCardRowType={NestedCardRowType.NonNested}
              >
                {memberPlans.map(({memberPlan, memberErrors}) => (
                  <ImportPlanSectionRow
                    key={memberPlan.objectId}
                    plan={memberPlan}
                    errors={memberErrors}
                    onUpdatePlan={(updatedPlan) => {
                      props.onUpdatePlans([updatedPlan]);
                    }}
                    nestedDepth={1}
                    showActionInline={true}
                    highImpactChangeMap={props.highImpactChangeMap}
                  />
                ))}
              </ChildImportPlanSectionWrapper>
            )
          );
        })}
    </>
  );
}

function getPlansByTaskSchema(
  plans: Array<PrimaryObjectPlan>,
  objectType: PrimaryObjectType
): {[key: string]: Array<PrimaryObjectPlan>} {
  return plans
    .filter((plan) => plan.objectType === objectType)
    .reduce(
      (acc, curr) => {
        const attrs = curr.objectAttributes as {processTaskSchemaId: string};
        assert(attrs.processTaskSchemaId != null, 'Expected processTaskSchemaId attribute to be defined');
        if (acc[attrs.processTaskSchemaId] == null) {
          acc[attrs.processTaskSchemaId] = [];
        }
        acc[attrs.processTaskSchemaId].push(curr);
        return acc;
      },
      {} as {[key: string]: Array<PrimaryObjectPlan>}
    );
}

export default function ProcessTaskSchemaPlanSection(props: ProcessTaskSchemaPlanSectionProps): JSX.Element {
  const taskSchemaPlans = props.plans.filter(
    (plan) => plan.objectType === PrimaryObjectType.ProcessTaskSchema
  );
  const memberPlansByObjectTypeAndTaskSchema = Object.keys(MEMBER_PLAN_CONFIGS_BY_TYPE).reduce(
    (acc, objectType: PrimaryObjectType) => {
      acc[objectType] = getPlansByTaskSchema(props.plans, objectType);
      return acc;
    },
    {} as {[objectType in PrimaryObjectType]?: {[key: string]: Array<PrimaryObjectPlan>}}
  );

  const sortedMemberPlanConfigsByType = Object.keys(MEMBER_PLAN_CONFIGS_BY_TYPE).sort(
    (leftObjectType: PrimaryObjectType, rightObjectType: PrimaryObjectType) =>
      MEMBER_PLAN_CONFIGS_BY_TYPE[leftObjectType]!.title.localeCompare(
        MEMBER_PLAN_CONFIGS_BY_TYPE[rightObjectType]!.title
      )
  );

  const getMemberPlanAndErrors = (taskSchemaPlan: PrimaryObjectPlan): Array<MemberObjectPlanAndError> =>
    compact(
      sortedMemberPlanConfigsByType
        .map((objectType: PrimaryObjectType) => {
          const memberPlans = memberPlansByObjectTypeAndTaskSchema[objectType]?.[taskSchemaPlan.objectId];
          return memberPlans?.map((memberPlan) => ({
            memberPlan,
            memberErrors: props.errorsByObjectId?.[memberPlan.objectId] ?? [],
          }));
        })
        .flat()
    );

  return (
    <ImportPlanSectionWrapper {...props} plansToSummarize={taskSchemaPlans}>
      <>
        {taskSchemaPlans.map((taskSchemaPlan) => (
          <ProcessTaskSchemaPlanRow
            key={taskSchemaPlan.objectId}
            plan={taskSchemaPlan}
            memberPlansAndErrors={getMemberPlanAndErrors(taskSchemaPlan)}
            errors={props.errorsByObjectId[taskSchemaPlan.objectId] || []}
            onUpdatePlans={(updatedPlans) => {
              props.onUpdatePlans(updatedPlans);
            }}
            highImpactChangeMap={props.highImpactChangeMap}
          />
        ))}
      </>
    </ImportPlanSectionWrapper>
  );
}
